import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, LayoutTemplate } from 'lucide-react';
import { useNotify } from 'react-admin';
import { deleteNcrTemplate, getAllNcrTemplates, type NcrTemplate } from '../../../services/ncrTemplateService.ts';
import './NcrTemplateList.css';

const NcrTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllNcrTemplates();
      setTemplates(data);
    } catch (error) {
      notify('Failed to load templates', { type: 'warning' });
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteNcrTemplate(id);
        notify('Template deleted successfully');
        fetchTemplates();
      } catch (error) {
        notify('Failed to delete template', { type: 'warning' });
        console.error('Error deleting template:', error);
      }
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (template.description?.toLowerCase().includes(searchText.toLowerCase()) || false);
    const matchesFilter =
      filterActive === 'all' ||
      (filterActive === 'active' && template.isActive) ||
      (filterActive === 'inactive' && !template.isActive);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="nl-container">
      {/* Header */}
      <div className="nl-header">
        <div className="nl-header-left">
          <p className="nl-eyebrow">NCR Configuration</p>
          <h1 className="nl-title">Template Management</h1>
        </div>
        <button className="nl-create-btn" onClick={() => navigate('/NcrTemplate/create')}>
          <Plus size={16} />
          Create Template
        </button>
      </div>

      {/* Toolbar */}
      <div className="nl-toolbar">
        <div className="nl-search">
          <Search size={16} className="nl-search-icon" />
          <input
            type="text"
            placeholder="Search template name or description…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button className="nl-search-clear" onClick={() => setSearchText('')}>✕</button>
          )}
        </div>
        <select
          className="nl-filter-select"
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <span className="nl-count">
          {filteredTemplates.length} of {templates.length} records
        </span>
      </div>

      {/* Table area */}
      <div className="nl-body">
        {loading ? (
          <div className="nl-state">
            <div className="nl-spinner" />
            <p>Loading templates…</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="nl-state">
            <LayoutTemplate size={48} className="nl-empty-icon" />
            <p className="nl-empty-title">
              {searchText ? `No results for "${searchText}"` : 'No templates yet'}
            </p>
            <p className="nl-empty-sub">
              {searchText
                ? 'Try a different search term.'
                : 'Create your first template to get started.'}
            </p>
            {!searchText && (
              <button className="nl-create-btn" onClick={() => navigate('/NcrTemplate/create')}>
                <Plus size={16} /> Create Template
              </button>
            )}
          </div>
        ) : (
          <table className="nl-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Stages</th>
                <th>Properties</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <tr
                  key={template.id}
                  className="nl-row"
                  onClick={() => navigate(`/NcrTemplate/${template.id}/view`)}
                >
                  <td>
                    <span className="nl-ncr-number">{template.name}</span>
                  </td>
                  <td className="ntl-desc">{template.description || '—'}</td>
                  <td>
                    <span className="nl-badge nl-badge--open">{template.stages?.length || 0}</span>
                  </td>
                  <td>
                    <span className="nl-badge nl-badge--open">{template.properties?.length || 0}</span>
                  </td>
                  <td>
                    <span className={`nl-badge ${template.isActive ? 'nl-badge--close' : 'nl-badge--inactive'}`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="nl-muted">
                    {template.createdAt ? new Date(template.createdAt).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="nl-actions">
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                        title="View"
                        onClick={() => navigate(`/NcrTemplate/${template.id}/view`)}
                      >
                        View
                      </button>
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
                        title="Edit"
                        onClick={() => navigate(`/NcrTemplate/${template.id}/edit`)}
                      >
                        Edit
                      </button>
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                        onClick={() => template.id != null && handleDelete(template.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NcrTemplateList;
